import { ArrowLeft, Save } from 'lucide-react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const RMCBuildingAdd = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm();

  // 임대사업자 목록 가져오기
  const { data: propertyOwners, isLoading: isLoadingOwners, error: ownersError } = useQuery(
    'rmc-property-owners',
    async () => {
      const response = await api.get('/rmc/property-owners');
      console.log('🔍 임대사업자 목록 응답:', response);
      return response;
    }
  );

  console.log('🔍 propertyOwners 데이터:', propertyOwners);
  console.log('🔍 isLoadingOwners:', isLoadingOwners);
  console.log('🔍 ownersError:', ownersError);

  // 건물 추가 뮤테이션
  const addBuildingMutation = useMutation(
    (data) => api.post('/rmc/buildings', data),
    {
      onSuccess: () => {
        toast.success('건물이 성공적으로 등록되었습니다.');
        queryClient.invalidateQueries('rmc-buildings');
        queryClient.invalidateQueries('rmc-buildings-statistics');
        navigate('/rmc/buildings');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '건물 등록에 실패했습니다.');
        setIsSubmitting(false);
      }
    }
  );

  const onSubmit = (data) => {
    setIsSubmitting(true);
    
    // 데이터 변환
    const processedData = {
      ...data,
      property_owner_id: parseInt(data.property_owner_id),
      total_floors: data.total_floors ? parseInt(data.total_floors) : null,
      total_units: data.total_units ? parseInt(data.total_units) : null,
      year_built: data.year_built ? parseInt(data.year_built) : null,
      total_floor_area: data.total_floor_area ? parseFloat(data.total_floor_area) : null,
      land_area: data.land_area ? parseFloat(data.land_area) : null,
      building_area: data.building_area ? parseFloat(data.building_area) : null,
      floor_area_ratio: data.floor_area_ratio ? parseFloat(data.floor_area_ratio) : null,
      building_coverage_ratio: data.building_coverage_ratio ? parseFloat(data.building_coverage_ratio) : null,
      parking_spaces: data.parking_spaces ? parseInt(data.parking_spaces) : null,
      elevator_count: data.elevator_count ? parseInt(data.elevator_count) : null,
      monthly_maintenance_fee: data.monthly_maintenance_fee ? parseInt(data.monthly_maintenance_fee) : null,
      heating_fee: data.heating_fee ? parseInt(data.heating_fee) : null,
      electric_capacity: data.electric_capacity ? parseInt(data.electric_capacity) : null,
      water_meter_count: data.water_meter_count ? parseInt(data.water_meter_count) : null,
      // 불린 값 변환
      has_elevator: data.has_elevator === "true" || data.has_elevator === true,
      has_cctv: data.has_cctv === "true" || data.has_cctv === true,
    };
    
    console.log('RMC 건물 등록 데이터:', processedData);
    console.log('RMC 건물 등록 데이터 (JSON):', JSON.stringify(processedData, null, 2));
    addBuildingMutation.mutate(processedData);
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* 헤더 */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/rmc/buildings')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          건물 목록으로 돌아가기
        </button>
        <h1 className="text-2xl font-bold text-gray-900">건물 등록</h1>
        <p className="text-gray-600">새로운 건물을 등록합니다</p>
      </div>

      {/* 폼 */}
      <div className="bg-white rounded-lg shadow">
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* 기본 정보 */}
          <div className="border-b border-gray-200 pb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">기본 정보</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  임대사업자 <span className="text-red-500">*</span>
                </label>
                <select
                  {...register('property_owner_id', { required: '임대사업자를 선택해주세요' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">임대사업자를 선택하세요</option>
                  {propertyOwners && Array.isArray(propertyOwners) && propertyOwners.map((owner) => (
                    <option key={owner.id} value={owner.id}>
                      {owner.name} ({owner.company_name || '개인'})
                    </option>
                  ))}
                </select>
                {errors.property_owner_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.property_owner_id.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  건물명 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('name', { required: '건물명을 입력해주세요' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="건물명을 입력하세요"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  주소 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('address', { required: '주소를 입력해주세요' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="건물 주소를 입력하세요"
                />
                {errors.address && (
                  <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  건물 유형
                </label>
                <select
                  {...register('building_type')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">건물 유형을 선택하세요</option>
                  <option value="아파트">아파트</option>
                  <option value="오피스텔">오피스텔</option>
                  <option value="빌라">빌라</option>
                  <option value="원룸">원룸</option>
                  <option value="상가">상가</option>
                  <option value="기타">기타</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  용도
                </label>
                <select
                  {...register('building_purpose')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">용도를 선택하세요</option>
                  <option value="주거">주거</option>
                  <option value="상업">상업</option>
                  <option value="사무">사무</option>
                  <option value="공업">공업</option>
                  <option value="기타">기타</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  건물 코드
                </label>
                <input
                  type="text"
                  {...register('building_code')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="건물 고유 코드"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  연면적 (㎡)
                </label>
                <input
                  type="number"
                  {...register('total_floor_area', { min: 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="연면적"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  구조
                </label>
                <select
                  {...register('building_structure')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">구조를 선택하세요</option>
                  <option value="철근콘크리트">철근콘크리트</option>
                  <option value="철골">철골</option>
                  <option value="철골철근콘크리트">철골철근콘크리트</option>
                  <option value="블록">블록</option>
                  <option value="목조">목조</option>
                  <option value="기타">기타</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  엘리베이터 유무
                </label>
                <select
                  {...register('has_elevator')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value={false}>없음</option>
                  <option value={true}>있음</option>
                </select>
              </div>
            </div>
          </div>

          {/* 건물 상세 정보 */}
          <div className="border-b border-gray-200 pb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">건물 상세 정보</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  총 층수
                </label>
                <input
                  type="number"
                  {...register('total_floors', { min: 1 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="총 층수"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  총 세대수
                </label>
                <input
                  type="number"
                  {...register('total_units', { min: 1 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="총 세대수"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  건축년도
                </label>
                <input
                  type="number"
                  {...register('year_built', { min: 1900, max: new Date().getFullYear() })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="건축년도"
                />
              </div>
            </div>
          </div>

          {/* 시설 정보 */}
          <div className="border-b border-gray-200 pb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">시설 정보</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  전기용량 (kW)
                </label>
                <input
                  type="number"
                  {...register('electric_capacity', { min: 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="전기용량"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  수도계량기 수
                </label>
                <input
                  type="number"
                  {...register('water_meter_count', { min: 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="수도계량기 수"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  가스방식
                </label>
                <select
                  {...register('gas_type')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">가스방식을 선택하세요</option>
                  <option value="도시가스">도시가스</option>
                  <option value="LPG">LPG</option>
                  <option value="프로판가스">프로판가스</option>
                  <option value="기타">기타</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  난방형태
                </label>
                <select
                  {...register('heating_type')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">난방형태를 선택하세요</option>
                  <option value="개별난방">개별난방</option>
                  <option value="중앙난방">중앙난방</option>
                  <option value="지역난방">지역난방</option>
                  <option value="기타">기타</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CCTV 유무
                </label>
                <select
                  {...register('has_cctv')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value={false}>없음</option>
                  <option value={true}>있음</option>
                </select>
              </div>
            </div>
          </div>

          {/* 버튼 */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/rmc/buildings')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  등록 중...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  건물 등록
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RMCBuildingAdd;

